<script>
import { handleError } from "@/utils/error";
import { logMixins } from "@/modules/logs/mixins";
import logsUtil from "@/modules/logs/util";
import MessagingLogMap from "./messaging-log-map.vue";

export default {
  name: "MessagingLogTable",
  mixins: [logMixins],
  components: {
    MessagingLogMap
  },
  data() {
    return {
      logsUtil,
      headers: [
        {
          text: this.$t("modules.logs.messagingLog.logTime"),
          align: "start",
          value: "time_logged"
        },
        {
          text: this.$t("modules.logs.messagingLog.satcomTimestamp"),
          align: "left",
          value: "time_sent"
        },
        {
          text: this.$t("modules.logs.messagingLog.haloName"),
          align: "start",
          value: "device_details.device_name"
        },
        {
          text: this.$t("modules.logs.messagingLog.haloSN"),
          align: "start",
          value: "device_details.device_serial"
        },
        {
          text: this.$t("modules.logs.messagingLog.satcomIMEI"),
          align: "center",
          value: "satcom_imei"
        },
        {
          text: this.$t("modules.logs.messagingLog.network.title"),
          align: "start",
          value: "network"
        },
        {
          text: this.$t("modules.logs.messagingLog.severity"),
          align: "start",
          value: "severity"
        },
        {
          text: this.$t("modules.logs.messagingLog.description"),
          align: "start",
          value: "message_hex"
        },
        {
          text: this.$t("modules.logs.messagingLog.latitude"),
          align: "start",
          value: "latitude"
        },
        {
          text: this.$t("modules.logs.messagingLog.longitude"),
          align: "center",
          value: "longitude"
        },
        {
          text: this.$t("modules.logs.messagingLog.altitude"),
          align: "start",
          value: "altitude"
        },
        {
          text: "",
          align: "center",
          value: "map"
        }
      ],
      loadItems: [],
      height: 0,
      rows: 15,
      options: {},
      loading: false
    };
  },
  computed: {
    satcomMessages() {
      return this.$store.state.messagingLog.satcom_messages;
    },
    rowsCount() {
      return this.loadItems.length;
    },
    dialog: {
      get() {
        return this.$store.state.messagingLog.dialog;
      },
      set(val) {
        this.$store.commit("messagingLog/updateDialog", val);
      }
    },
    selected: {
      get() {
        return this.$store.state.messagingLog.selected;
      },
      set(val) {
        this.$store.commit("messagingLog/updateSelected", val);
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.options.itemsPerPage = this.rows;
      this.loading = true;
      this.$store
        .dispatch("messagingLog/fetchAll")
        .then(() => {
          this.loadItems = this.satcomMessages;
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    scroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (this.options.itemsPerPage < this.satcomMessages.length) {
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          this.paginate();
        }
      }
    },
    paginate() {
      this.options.itemsPerPage = this.options.itemsPerPage + this.rows;
    },
    parseNetworks(network) {
      switch (network) {
        case 1:
          return this.$t("modules.logs.messagingLog.network.iridium");
        default:
          return "";
      }
    },
    showMap(item) {
      this.dialog = true;
      const coordinate = {
        lat: item.location.coordinate[1],
        lng: item.location.coordinate[0]
      };
      this.selected = {
        coordinate: coordinate,
        altitude: item.location.altitude,
        device_name: item.device_details.device_name
      };
    },
    convertHexToString(hex) {
      let isString = typeof hex === "string";
      if (!isString) return;
      let str = "";
      for (var i = 0; i < hex.length; i += 2) {
        let hex_substring = hex.substr(i, 2);
        let char_code = parseInt(hex_substring, 16);
        let char = String.fromCharCode(char_code);
        str += char;
      }
      return str;
    }
  }
};
</script>

<template>
  <div class="mt-4">
    <v-flex d-flex align-center>
      <v-subheader class="font-weight-bold">
        <span>
          <span id="messaging-log__total-string">Total:</span>
          <span id="messaging-log__rows-count">{{ rowsCount }}</span>
        </span>
      </v-subheader>
    </v-flex>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      :loading="loading"
      class="elevation-1 mx-2 mt-3 scroll"
      hide-default-footer
      style="min-height: 100%;"
      id="messaging-log__table"
      fixed-header
      :options.sync="options"
      must-sort
      :height="height - 15 + 'px'"
    >
      <template v-slot:body.append>
        <td
          colspan="3"
          class="text-center py-2"
          v-if="options.itemsPerPage < loadItems.length"
        >
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </td>
      </template>
      <template v-slot:item.time_logged="props">
        <span>{{ parseLogDatetime(props.item.time_logged) }}</span>
      </template>
      <template v-slot:item.time_sent="props">
        <span>{{ parseLogDatetime(props.item.time_sent) }}</span>
      </template>
      <template v-slot:item.network="props">
        {{ parseNetworks(props.item.network) }}
      </template>
      <template v-slot:item.severity="props">
        <v-chip
          small
          :color="logsUtil.parseColor(props.item.severity).toString()"
          text-color="logs__severity_chip_color"
          >{{ logsUtil.parseSeverity(props.item.severity) }}</v-chip
        >
      </template>
      <template v-slot:item.latitude="props">
        <span>{{ props.item.location.coordinate[1] }}&deg;</span>
      </template>
      <template v-slot:item.longitude="props">
        <span>{{ props.item.location.coordinate[0] }}&deg;</span>
      </template>
      <template v-slot:item.altitude="props">
        <span>{{ props.item.location.altitude }} m</span>
      </template>
      <template v-slot:item.map="props">
        <v-btn small color="primary" @click="showMap(props.item)">
          {{ $t("modules.logs.messagingLog.map") }}
        </v-btn>
      </template>
      <template v-slot:item.message_hex="props">
        <span>{{ convertHexToString(props.item.message_hex) }}</span>
      </template>
    </v-data-table>
    <messaging-log-map></messaging-log-map>
  </div>
</template>
